﻿import { useState, useCallback, useRef, useEffect } from 'react';

interface UseInfiniteScrollOptions<T = unknown> {
  pageSize?: number;
  threshold?: number; // px from bottom to trigger load
  /**
   * Chiave univoca per elemento. Se fornita, gli elementi duplicati (stessa
   * chiave) vengono scartati in fase di append: il backend può restituire lo
   * stesso record su pagine consecutive e senza dedup React segnala chiavi
   * duplicate (e potrebbe duplicare/omettere righe).
   */
  getItemKey?: (item: T) => string | number;
}

interface UseInfiniteScrollResult<T> {
  items: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  total: number;
  reset: () => void;
  setFetchFn: (fn: FetchFn<T>) => void;
  sentinelRef: (node: HTMLElement | null) => void;
}

type FetchFn<T> = (
  page: number,
  pageSize: number,
  signal: AbortSignal,
) => Promise<{ items: T[]; total: number }>;

export function useInfiniteScroll<T>(options: UseInfiniteScrollOptions<T> = {}): UseInfiniteScrollResult<T> {
  const { pageSize = 25, threshold = 200, getItemKey } = options;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchFnRef = useRef<FetchFn<T> | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isFetchingRef = useRef(false);

  // Mantiene l'ultima getItemKey senza invalidare loadPage.
  const getItemKeyRef = useRef(getItemKey);
  getItemKeyRef.current = getItemKey;

  // Generation counter: incremented on every setFetchFn / reset call.
  // Responses from a stale generation are discarded.
  const generationRef = useRef(0);

  // AbortController for the current in-flight request
  const abortRef = useRef<AbortController | null>(null);

  const loadPage = useCallback(async (
    fn: FetchFn<T>,
    pageNum: number,
    isFirst: boolean,
    gen: number,
  ) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    // Abort any previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (isFirst) setLoading(true);
    else setLoadingMore(true);

    try {
      const result = await fn(pageNum, pageSize, controller.signal);

      // If generation changed while we were awaiting, discard the result
      if (gen !== generationRef.current) return;

      const newItems = result.items || [];
      const newTotal = result.total || 0;

      setTotal(newTotal);

      const getKey = getItemKeyRef.current;

      if (isFirst) {
        setItems(getKey ? dedupeByKey(newItems, getKey) : newItems);
      } else if (getKey) {
        // Append scartando gli elementi già presenti (per chiave univoca).
        setItems(prev => {
          const seen = new Set(prev.map(getKey));
          const merged = [...prev];
          for (const it of newItems) {
            const k = getKey(it);
            if (!seen.has(k)) { seen.add(k); merged.push(it); }
          }
          return merged;
        });
      } else {
        setItems(prev => [...prev, ...newItems]);
      }

      const totalLoaded = isFirst ? newItems.length : (pageNum - 1) * pageSize + newItems.length;
      setHasMore(totalLoaded < newTotal && newItems.length > 0);
      setPage(pageNum);
    } catch (err: unknown) {
      // Ignore aborted requests
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // Stale generation — ignore silently
      if (gen !== generationRef.current) return;

      if (isFirst) setItems([]);
      setHasMore(false);
    } finally {
      // Only clear flags if this is still the current generation
      if (gen === generationRef.current) {
        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;
      }
    }
  }, [pageSize]);

  const setFetchFn = useCallback((fn: FetchFn<T>) => {
    // Bump generation to invalidate any in-flight request
    const gen = ++generationRef.current;

    // Abort previous request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;

    fetchFnRef.current = fn;
    setItems([]);
    setPage(1);
    setHasMore(true);
    setTotal(0);
    isFetchingRef.current = false;

    loadPage(fn, 1, true, gen);
  }, [loadPage]);

  const loadNext = useCallback(() => {
    if (!hasMore || isFetchingRef.current || !fetchFnRef.current) return;
    loadPage(fetchFnRef.current, page + 1, false, generationRef.current);
  }, [hasMore, page, loadPage]);

  // IntersectionObserver sentinel ref
  // Guard: don't trigger loadNext while the first page is still loading
  const sentinelRef = useCallback((node: HTMLElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
          loadNext();
        }
      },
      { rootMargin: threshold + 'px' }
    );

    if (node) observerRef.current.observe(node);
  }, [hasMore, loadNext, threshold]);

  const reset = useCallback(() => {
    const gen = ++generationRef.current;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;

    setItems([]);
    setPage(1);
    setHasMore(true);
    setTotal(0);
    isFetchingRef.current = false;

    if (fetchFnRef.current) {
      loadPage(fetchFnRef.current, 1, true, gen);
    }
  }, [loadPage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { items, loading, loadingMore, hasMore, total, reset, setFetchFn, sentinelRef };
}

/** Rimuove i duplicati da una lista mantenendo il primo elemento per chiave. */
function dedupeByKey<T>(list: T[], getKey: (item: T) => string | number): T[] {
  const seen = new Set<string | number>();
  const result: T[] = [];
  for (const it of list) {
    const k = getKey(it);
    if (!seen.has(k)) { seen.add(k); result.push(it); }
  }
  return result;
}
